import { useCallback, useEffect, useState } from 'react'
import {
  DEFAULT_PAGE_SIZE,
  FILE_SORT_COLUMNS,
  FileSort,
  FileSortColumn,
  PAGE_SIZE_OPTIONS,
  SORT_DIRECTIONS,
  SortDirection,
} from '@/utils/fileSorting'

/**
 * Persists the file table's sort column/direction and page size across sessions.
 *
 * Each scope gets its own key, so sorting the file browser by size never
 * disturbs the recently-viewed list's recency order.
 */

export type FileTablePreferenceScope = 'explorer' | 'recent'

interface StoredPreferences {
  v: 1
  sort: FileSort | null
  pageSize: number
}

interface FileTablePreferences {
  sort: FileSort | null
  setSort: (sort: FileSort | null) => void
  pageSize: number
  setPageSize: (pageSize: number) => void
}

const STORAGE_VERSION = 1

const storageKey = (scope: FileTablePreferenceScope) => `file-table-prefs:${scope}`

/**
 * Accept a stored sort only if it still names a column we support — a
 * preference left behind by a renamed or removed column degrades to the
 * fallback instead of rendering an unsorted table.
 */
const parseSort = (value: unknown, fallback: FileSort | null): FileSort | null => {
  if (value === null) return null
  if (!value || typeof value !== 'object') return fallback

  const { column, direction } = value as { column?: unknown; direction?: unknown }

  if (!FILE_SORT_COLUMNS.includes(column as FileSortColumn)) return fallback
  if (!SORT_DIRECTIONS.includes(direction as SortDirection)) return fallback

  return { column: column as FileSortColumn, direction: direction as SortDirection }
}

const parsePageSize = (value: unknown): number =>
  PAGE_SIZE_OPTIONS.includes(value as (typeof PAGE_SIZE_OPTIONS)[number])
    ? (value as number)
    : DEFAULT_PAGE_SIZE

const readPreferences = (
  scope: FileTablePreferenceScope | undefined,
  fallbackSort: FileSort | null
): StoredPreferences => {
  const defaults: StoredPreferences = {
    v: STORAGE_VERSION,
    sort: fallbackSort,
    pageSize: DEFAULT_PAGE_SIZE,
  }

  if (!scope || typeof window === 'undefined') return defaults

  try {
    const raw = window.localStorage.getItem(storageKey(scope))
    if (!raw) return defaults

    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || parsed.v !== STORAGE_VERSION) return defaults

    // Recover per field, so one bad value doesn't discard the other.
    return {
      v: STORAGE_VERSION,
      sort: parseSort(parsed.sort, fallbackSort),
      pageSize: parsePageSize(parsed.pageSize),
    }
  } catch {
    // Malformed JSON, or storage unavailable (Safari private mode throws).
    return defaults
  }
}

export function useFileTablePreferences(
  scope: FileTablePreferenceScope | undefined,
  fallbackSort: FileSort | null
): FileTablePreferences {
  const [preferences, setPreferences] = useState<StoredPreferences>(() =>
    readPreferences(scope, fallbackSort)
  )

  useEffect(() => {
    if (!scope || typeof window === 'undefined') return

    try {
      window.localStorage.setItem(storageKey(scope), JSON.stringify(preferences))
    } catch {
      // Quota exceeded or storage disabled — the preference just won't persist.
    }
  }, [scope, preferences])

  const setSort = useCallback((sort: FileSort | null) => {
    setPreferences((prev) => ({ ...prev, sort }))
  }, [])

  const setPageSize = useCallback((pageSize: number) => {
    setPreferences((prev) => ({ ...prev, pageSize: parsePageSize(pageSize) }))
  }, [])

  return {
    sort: preferences.sort,
    setSort,
    pageSize: preferences.pageSize,
    setPageSize,
  }
}
